import React from 'react'
import PropTypes from 'prop-types'
import {
  AppProvider,
  Frame,
  Page,
  Navigation,
  TopBar,
  Link,
  Modal,
  TextContainer,
  Button,
  Card,
  ActionList,
  Spinner,
} from '@shopify/polaris'
import { NavMenu } from '@shopify/app-bridge-react'
import {
  BillingStatementDollarMajorMonotone,
  ReceiptMajorMonotone,
  ToolsMajorMonotone,
  PaymentsMajorMonotone,
  CustomersMajorMonotone,
  EmailMajorMonotone,
  NoteMajorMonotone,
  CardReaderChipMajorMonotone,
  TransactionMajorMonotone,
  OrdersMajorMonotone,
  ReportMinor,
} from '@shopify/polaris-icons'
import { ToastContainer, toast } from 'react-toastify'

import ChangelogModal from './ChangelogModal'

// http defaults
import { http } from '../utils/api'

import '@shopify/polaris/styles.css'
import UsageBanner from './UsageBanner'
import { Alert, Flex } from 'antd'
import OffersBanner from './OffersBanner'

class ShopifyPage extends React.Component {
  static contextTypes = {
    polaris: PropTypes.object,
  }

  state = {
    showReviewModal: false,
    showChangelogModal: false,
    isSearchActive: false,
    searchValue: '',
    searchResults: [],
    loading: false,
    timeout: 0,
    urlSuffix: '',
    showNavBar: false,
  }

  toggleReviewModal = () => {
    this.setState((prevState) => ({
      showReviewModal: !this.state.showReviewModal,
    }))
  }

  toggleChangelogModal = () => {
    this.setState((prevState) => ({
      showChangelogModal: !this.state.showChangelogModal,
    }))
  }

  componentWillMount() {
    this.setState((prevState) => ({
      showNavBar: this.props.layout === 'admin' ? true : false,
      urlSuffix: `?shop=${this.props.shop}&layout=${this.props.layout}`,
    }))

    http.defaults.headers.common['X-CSRF-Token'] = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute('content')
  }

  render() {
    const {
      pageTitle,
      subtitle,
      pageType,
      children,
      breadcrumbs,
      primaryAction,
      shop,
      showUsageBanner = false,
      showOffersBanner = false
    } = this.props

    const { isSearchActive, searchValue, searchResults, loading } = this.state

    let appProviderProps = {}

    const theme = {
      colors: {
        topBar: {
          background: '#1083ff',
        },
      },
    }

    const userMenuMarkup = (
      <div className="Top-Menu">
        <Flex align={'center'}>
          <div>
            <div id="google_translate_element"></div>
          </div>
          <Link
            url={`/pricing?shop=${this.props.shop}&layout=${this.props.layout}`}
          >
            <div className="Top-Menu__Item">Pricing</div>
          </Link>
          <Link url={`https://invoicefalcon.com/help`} external>
            <div className="Top-Menu__Item">Help Center</div>
          </Link>
        </Flex>
      </div>
    )

    const search = (query) => {
      http
        .get(`/api/invoices/search.json?q=${query}`)
        .then((response) =>
          this.setState({
            searchResults: response.data.invoices,
            loading: false,
          })
        )
        .catch((error) => console.log(error))
    }

    const handleSearchChange = (searchValue) => {
      this.setState((prevState) => ({
        searchValue: searchValue,
        loading: true,
      }))
      if (this.state.timeout) clearTimeout(this.state.timeout)
      var timeout = setTimeout(() => {
        search(this.state.searchValue)
        this.setState((prevState) => ({
          isSearchActive: this.state.searchValue.length,
        }))
      }, 1000)
      this.setState((prevState) => ({
        timeout: timeout,
      }))
    }

    const handleSearchResultsDismiss = () => {
      this.setState({
        isSearchActive: false,
        searchValue: '',
      })
    }

    const searchFieldMarkup = (
      <TopBar.SearchField
        onChange={handleSearchChange}
        value={searchValue}
        placeholder="Search documents"
      />
    )

    const handleSearch = () => {
      if (window.location.search == '') {
        if (window.location.href.includes('invoices')) {
          window.open(
            `${window.location.href}?search=${this.state.searchValue}`
          )
        } else {
          window.open(
            `${window.location.origin}/invoices?search=${this.state.searchValue}`
          )
        }
      } else {
        window.location.href = `${window.location.pathname}?search=${this.state.searchValue}`
      }
    }

    const searchResultsMarkup = (
      <Card>
        {loading ? (
          <p style={{ textAlign: 'center', marginTop: 10, marginBottom: 10 }}>
            <Spinner size="small" color="teal" />
          </p>
        ) : searchResults.length > 0 ? (
          <>
            <Button primary fullWidth onClick={handleSearch}>
              Search
            </Button>
            <ActionList
              items={searchResults.map((result) => ({
                content: result.number,
                url: result.url,
                external: true,
              }))}
            />
          </>
        ) : (
          <Card.Section>
            <p style={{ textAlign: 'center', marginTop: 10, marginBottom: 10 }}>
              No invoices found
            </p>
          </Card.Section>
        )}
      </Card>
    )

    const navigationMarkup = (
      <Navigation location="/">
        <Navigation.Section
          title="Documents"
          items={[
            {
              url: '/invoice/invoices' + this.state.urlSuffix,
              label: 'Invoices',
              icon: BillingStatementDollarMajorMonotone,
              selected: pageType == 'invoice' || pageType == 'all',
            },
            {
              url: '/quote/invoices/' + this.state.urlSuffix,
              label: 'Quotes',
              icon: NoteMajorMonotone,
              selected: pageType == 'drafts' || pageType == 'quote',
            },
            {
              url: '/credit_note/invoices/' + this.state.urlSuffix,
              label: 'Credit Notes',
              icon: CardReaderChipMajorMonotone,
              selected: pageType == 'credit_note',
            },
            {
              url: '/pro_forma/invoices/' + this.state.urlSuffix,
              label: 'Pro Forma Invoices',
              icon: ReceiptMajorMonotone,
              selected: pageType == 'pro_forma',
            },
            {
              url: '/orders' + this.state.urlSuffix,
              label: 'Shopify orders',
              icon: OrdersMajorMonotone,
              selected: pageType == 'orders',
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              url: '/customers' + this.state.urlSuffix,
              label: 'Customers',
              icon: CustomersMajorMonotone,
              selected: pageType == 'customer',
            },
          ]}
          separator
        />
        <Navigation.Section
          items={[
            {
              url: '/bulk_actions' + this.state.urlSuffix,
              label: 'Bulk Actions',
              icon: ReportMinor,
              selected: pageType == 'bulk_actions',
            },
          ]}
          separator
        />
        <Navigation.Section
          title="Settings"
          items={[
            {
              url: '/design' + this.state.urlSuffix,
              label: 'Design Invoice',
              icon: ToolsMajorMonotone,
              selected: pageType == 'customize',
            },
            {
              url: '/automatic_invoices' + this.state.urlSuffix,
              label: 'Automatic Invoices',
              icon: TransactionMajorMonotone,
              selected: pageType == 'automatic_invoices',
            },
            {
              url: '/settings' + this.state.urlSuffix,
              label: 'Email Settings',
              icon: EmailMajorMonotone,
              selected: pageType == 'settings',
            },
            {
              url: '/collectpayments' + this.state.urlSuffix,
              label: 'Payments',
              icon: PaymentsMajorMonotone,
              selected: pageType == 'payments',
            },
          ]}
          separator
        />
      </Navigation>
    )

    const topBarMarkup = (
      <TopBar
        showNavigationToggle={true}
        searchResults={searchResultsMarkup}
        searchResultsVisible={isSearchActive}
        onSearchResultsDismiss={handleSearchResultsDismiss}
        onNavigationToggle={() => {
          console.log('toggle navigation visibility')
        }}
      />
    )

    const reviewModal = (
      <Modal
        open={this.state.showReviewModal}
        onClose={() => this.toggleReviewModal()}
        title="Support our business"
      >
        <Modal.Section>
          <TextContainer>
            <p>
              Invoice Falcon is developed & maintained by 2 developers based out
              of Bangalore, India. 6 months ago, we gave up our full-time jobs
              to build simple, easy-to-use applications for Shopify users, like
              you, worldwide.
              <br />
              <br />
              If you enjoy using our application, we'd really appreciate it if
              you could take 1 minute of your time to write a review on the
              Shopify App Store. Your time and words will help us a lot in
              running our business. :)
            </p>
            <Button
              primary
              fullWidth
              size="large"
              url="https://apps.shopify.com/invoice-falcon?#modal-show=ReviewListingModal"
            >
              Write a review
            </Button>
            <br />
          </TextContainer>
        </Modal.Section>
      </Modal>
    )

    const changelogModal = (
      <ChangelogModal
        showChangelogModal={this.state.showChangelogModal}
        toggleChangelogModal={this.toggleChangelogModal}
      />
    )
    return (
      <AppProvider {...appProviderProps} theme={theme}>
        <Frame navigation={ this.props?.layout === 'admin' ? navigationMarkup : null}>
          {/* Warning Banner */}
          {/* {pageType === 'invoice' ? (
            <Alert
              type="warning"
              showIcon
              message="We are currently experiencing technical issues that may cause some emails to be delayed. Our team is working diligently to resolve the matter, and we appreciate your patience."
              closable
              banner
            />
          ) : null} */}

          {/* {showOffersBanner ? <OffersBanner /> : null} */}
          
          {showUsageBanner ? <UsageBanner /> : null}

          {/* New Nav Menu */}
          <div style={{ display: 'none' }}>
            <NavMenu>
              <a href={`/?layout=${this.props.layout}`} rel="home"></a>
              <a href={`/invoice/invoices?layout=${this.props.layout}`}>
                Invoices
              </a>
              <a href={`/quote/invoices/?layout=${this.props.layout}`}>
                Quotes
              </a>
              <a href={`/credit_note/invoices/?layout=${this.props.layout}`}>
                Credit Notes
              </a>
              <a href={`/pro_forma/invoices/?layout=${this.props.layout}`}>
                Pro Forma Invoices
              </a>
              <a href={`/orders?layout=${this.props.layout}`}>Shopify orders</a>
              <a href={`/customers?layout=${this.props.layout}`}>Customers</a>
              <a href={`/bulk_actions?layout=${this.props.layout}`}>
                Bulk Actions
              </a>
              <a href={`/design?layout=${this.props.layout}`}>Design Invoice</a>
              <a href={`/automatic_invoices?layout=${this.props.layout}`}>
                Automatic Invoices
              </a>
              <a href={`/settings?layout=${this.props.layout}`}>
                Email Settings
              </a>
              <a href={`/collectpayments?layout=${this.props.layout}`}>
                Payments
              </a>
            </NavMenu>
          </div>
          <Page
            title={pageTitle}
            subtitle={subtitle}
            breadcrumbs={breadcrumbs}
            primaryAction={primaryAction}
          >
            {children}
            {reviewModal}
            {changelogModal}
          </Page>
          <ToastContainer position={toast.POSITION.BOTTOM_CENTER} />
        </Frame>
      </AppProvider>
    )
  }
}

export default ShopifyPage
